import { useState, useEffect } from "react";
import SEO from "../Components/SEO";
import { Loader2, Package } from "lucide-react";
import TheNav from "../Components/navbar";
import CreatorOrders from "../Components/creator/OrdersTable";
import EditorOrders from "../Components/editor/OrdersTable";
import { useAuth } from "../Context/AuthContext";
import { MINT, PURPLE } from "../Components/shared/OrdersTablePrimitives";

const Orders = () => {
  const { activeUser } = useAuth();
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const role = activeUser?.profileInfo?.role || "";
  const isCreator = role.toLowerCase() === "creator";

  useEffect(() => {
    if (activeUser) {
      setOrders(activeUser.orders || {});
      setLoading(false);
    }
  }, [activeUser]);

  return (
    <>
      <SEO
        title="Orders | Kre8"
        description="Private Kre8 order workspace for creators and editors."
        canonicalPath="/orders"
        noindex
      />

      <main className="relative min-h-screen overflow-hidden bg-[#07060d] text-white">
        <TheNav />

        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full opacity-25 blur-[140px]"
          style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
        />
        <div
          className="pointer-events-none absolute right-0 top-40 h-[380px] w-[520px] rounded-full opacity-20 blur-[120px]"
          style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
        />

        <div className="relative mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
          <div
            className="relative mb-6 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-7"
            style={{
              boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05), 0 20px 60px -30px ${PURPLE}66`,
            }}
          >
            <div
              className="pointer-events-none absolute -right-20 -top-24 h-48 w-48 rounded-full opacity-30 blur-3xl"
              style={{ background: PURPLE }}
            />
            <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 bg-white/5">
                    <Package size={14} style={{ color: MINT }} />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
                    Orders
                  </span>
                </div>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Your Orders
                </h1>
                <p className="mt-1.5 max-w-xl text-sm text-white/60">
                  {isCreator
                    ? "Track your active requests, deliveries, and completed work."
                    : "Manage active projects, deliveries, and earnings."}
                </p>
              </div>

              {role && (
                <span
                  className="inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
                  style={{
                    color: isCreator ? PURPLE : MINT,
                    background: isCreator ? `${PURPLE}1f` : `${MINT}1f`,
                    borderColor: isCreator ? `${PURPLE}40` : `${MINT}40`,
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{
                      background: isCreator ? PURPLE : MINT,
                      boxShadow: `0 0 8px ${isCreator ? PURPLE : MINT}`,
                    }}
                  />
                  {role}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="h-20 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]"
                />
              ))}
              <div className="flex justify-center pt-4">
                <Loader2 size={22} className="animate-spin text-white/40" />
              </div>
            </div>
          ) : isCreator ? (
            <CreatorOrders orders={orders} />
          ) : (
            <EditorOrders orders={orders} user={activeUser} setOrders={setOrders} />
          )}
        </div>
      </main>
    </>
  );
};

export default Orders;
